import { alpha, buttonClasses, inputBaseClasses } from '@mui/material';

import { HEADER_HEIGHT } from 'constants/components';

export const components = {
  MuiTypography: {
    defaultProps: {
      color: 'text.primary',
      variantMapping: {
        h1: 'h1',
        h2: 'h2',
        'h2-bold': 'h2',
        subtitle1: 'h3',
        'subtitle1-bold': 'h3',
        h5: 'h4',
        body1: 'p',
        body2: 'p',
        'body2-bold': 'p',
        button: 'span',
        caption: 'span',
        overline: 'span',
      },
    },
    styleOverrides: {
      h1: {
        wordBreak: 'break-word',
      },
    },
  },
  MuiButton: {
    defaultProps: {
      disableRipple: true,
      variant: 'contained',
      color: 'primary',
      loadingPosition: 'end',
    },
    styleOverrides: {
      root: ({ theme: { breakpoints, palette } }) => ({
        borderRadius: '10px',
        boxShadow: 'none',
        fontSize: 15,

        [breakpoints.up('md')]: {
          fontSize: 18,
        },

        '&:hover': {
          boxShadow: `0 0 16px 0 ${alpha(palette.colors.black, 0.2)}`,
        },

        '&.Mui-disabled': {
          color: palette.text.disabled,
          opacity: 0.5,
        },

        [`& .${buttonClasses.loadingIndicator}`]: {
          color: palette.text.secondary,
        },
      }),
      sizeSmall: ({ theme: { breakpoints } }) => ({
        paddingBlock: 5,
        paddingInline: 15,
        [breakpoints.up('md')]: {
          paddingBlock: 16,
          paddingInline: 20,
        },
      }),
      sizeMedium: ({ theme: { breakpoints } }) => ({
        paddingBlock: 10,
        paddingInline: 26,
        [breakpoints.up('md')]: {
          paddingBlock: 20,
          paddingInline: 25,
          minWidth: 200,
        },
      }),
      containedPrimary: ({ theme: { palette } }) => ({
        '&.Mui-disabled': {
          backgroundColor: palette.primary.main,
        },
      }),
      containedSecondary: ({ theme: { palette } }) => ({
        '&.Mui-disabled': {
          backgroundColor: palette.secondary.main,
        },
      }),
      containedInfo: ({ theme: { palette } }) => ({
        '&.Mui-disabled': {
          backgroundColor: palette.info.main,
        },
      }),
      containedSuccess: ({ theme: { palette } }) => ({
        '&.Mui-disabled': {
          backgroundColor: palette.success.main,
        },
      }),
      text: {
        '&:hover': {
          boxShadow: 'none',
          backgroundColor: 'transparent',
        },
      },
    },
  },
  MuiIconButton: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        boxShadow: 'none',

        '&:hover': {
          boxShadow: 'none',
          backgroundColor: 'unset',
        },
      },
    },
  },
  MuiLink: {
    defaultProps: {
      color: 'text.primary',
    },
    styleOverrides: {
      root: ({ theme: { palette } }) => ({
        textDecorationColor: palette.text.primary,
      }),
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme: { typography } }) => typography.body2,
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme: { breakpoints } }) => ({
        maxHeight: `calc(100vh - ${HEADER_HEIGHT * 2}px)`,
        [breakpoints.up('md')]: {
          maxHeight: `calc(100vh - ${HEADER_HEIGHT * 2 + 24}px)`,
        },
        [breakpoints.up('xl')]: {
          maxHeight: `calc(100vh - ${HEADER_HEIGHT * 2 + 48}px)`,
        },
        'h2, p': { wordBreak: 'break-word' },
      }),
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme: { palette, spacing } }) => ({
        maxWidth: 350,
        maxHeight: 250,
        overflowY: 'auto',
        backgroundColor: palette.text.primary,
        borderRadius: 10,
        fontSize: 16,
        padding: spacing(1, 2),
        cursor: 'default',
      }),
      arrow: ({ theme: { palette } }) => ({
        color: palette.text.primary,
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: ({ theme: { palette } }) => ({
        [`&.Mui-disabled, & .${inputBaseClasses.input}.Mui-disabled`]: {
          color: palette.colors.darkGrey,
          WebkitTextFillColor: palette.colors.darkGrey,
        },
      }),
    },
  },
  MuiPickerPopper: {
    styleOverrides: {
      paper: ({ theme: { palette } }) => ({
        border: `1px solid ${palette.colors.darkPale}`,
        borderRadius: 10,
        overflow: 'hidden',
        boxShadow: `0 4px 16px ${alpha(palette.colors.darkBlue, 0.12)}`,
      }),
    },
  },
  MuiDesktopDatePicker: {
    defaultProps: {
      views: ['day'],
    },
  },
  MuiDateCalendar: {
    defaultProps: {
      reduceAnimations: true,
      dayOfWeekFormatter: (date) => date.format('dd'),
    },
    styleOverrides: {
      root: {
        width: 290,
        height: 'auto',
        maxHeight: 'none',
      },
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      root: ({ theme: { palette, spacing } }) => ({
        position: 'relative',
        justifyContent: 'center',
        backgroundColor: palette.colors.lightPale,
        margin: 0,
        padding: spacing(1, 1, 1.5),
        minHeight: 'auto',
      }),
      labelContainer: {
        position: 'absolute',
        left: '50%',
        maxWidth: 'none',
        margin: 0,
        transform: 'translateX(-50%)',
      },
      label: ({ theme: { typography, palette } }) => ({
        ...typography.overline,
        fontWeight: 600,
        color: palette.text.primary,
        margin: 'unset',
      }),
      switchViewButton: {
        display: 'none',
      },
    },
  },
  MuiPickersArrowSwitcher: {
    styleOverrides: {
      root: {
        width: '100%',
      },
      spacer: {
        flex: 1,
      },
      button: ({ theme: { palette } }) => ({
        padding: 'unset',
        color: palette.primary.main,
      }),
    },
  },
  MuiDayCalendar: {
    styleOverrides: {
      header: ({ theme: { palette } }) => ({
        backgroundColor: palette.colors.lightPale,
        borderBottom: `1px solid ${palette.colors.darkPale}`,
        paddingBottom: 4,
      }),
      weekDayLabel: ({ theme: { typography, palette } }) => ({
        ...typography.overline,
        fontWeight: 600,
        color: palette.text.primary,
        height: 16,
      }),
      monthContainer: {
        paddingTop: 16,
      },
      slideTransition: {
        minHeight: 'auto',
      },
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: ({ theme: { typography, palette } }) => ({
        ...typography.body2,
        color: palette.text.primary,
        borderRadius: 5,
        '&.Mui-selected': {
          backgroundColor: palette.primary.main,
          color: palette.colors.white,
          '&:hover, &:focus': {
            backgroundColor: palette.primary.main,
          },
        },
        '&.MuiPickersDay-today': {
          borderColor: palette.primary.main,
        },
        '&.Mui-disabled:not(.Mui-selected)': {
          color: palette.colors.darkGrey,
        },
      }),
    },
  },
};
